import React, { useState, useEffect } from "react";
// Use the shared axios instance so the auth token is sent
// (the backend needs it to know whether the user is staff)
import axios from "../../Authservices/axiosInstance";
import {
  Row,
  Col,
  Button,
  Form,
  Dropdown,
  ButtonGroup,
  Modal,
  Card,
} from "react-bootstrap";
import { MdAddchart } from "react-icons/md";
import { Link } from "react-router-dom";
import "./emp-info.css";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { FcOk } from "react-icons/fc";
import { RiDeleteBin5Line } from "react-icons/ri";
import { FaEdit } from "react-icons/fa";
import { IoIosEye, IoIosArrowForward } from "react-icons/io";
import DOMPurify from "dompurify";
import { canSeeSalary, canDeleteEmployee, canEditEmployee } from "../../utils/permissions";

export default function EmployeeInformation() {
  // ===== Permissions =====
  const showSalary = canSeeSalary();
  const showDelete = canDeleteEmployee();

  // UPDATED: Adjusted column count since User ID is removed
  const totalColumns = showSalary ? 9 : 8;

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNextOfKinModal, setShowNextOfKinModal] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "https://backend.ems.uk.hiradentallab.com";

  // Sort employees alphabetically by name (A–Z)
  const sortEmployeesByName = (employees) => {
    return [...employees].sort((a, b) =>
      String(a.name || "").trim().localeCompare(String(b.name || "").trim(), "en", {
        sensitivity: "base",
      })
    );
  };

  // Fetch all active employees initially
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/user/getAllUsers`);

        // Filter out inactive users (case insensitive) and sort alphabetically
        const activeUsers = response.data.filter(user => user.status?.toLowerCase() === "active");
        const sortedUsers = sortEmployeesByName(activeUsers);

        setData(sortedUsers);
        setSearchResults(sortedUsers);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search query change and fetch search results
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchQuery.trim() === "") {
        const activeUsers = data.filter(user => user.status?.toLowerCase() === "active");
        setSearchResults(sortEmployeesByName(activeUsers));
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/user/search?query=${encodeURIComponent(searchQuery)}`
        );
        const activeSearchResults = response.data.filter(user => user.status?.toLowerCase() === "active");
        setSearchResults(sortEmployeesByName(activeSearchResults));
      } catch (err) {
        console.error("Error fetching search results:", err);
        setSearchResults([]);
      }
    };

    const timeoutId = setTimeout(fetchSearchResults, 500); // 500ms debounce delay
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, data]);

  // Go back to page 1 when the search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= Math.ceil(searchResults.length / rowsPerPage)
    ) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
  };

  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(searchResults.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleShow = (employeeData) => {
    setSelectedEmployee(employeeData);
    setShowDialog(true);
  };

  const handleClose = () => setShowDialog(false);

  const handleShowNextOfKin = (employeeData) => {
    setSelectedEmployee(employeeData);
    setShowNextOfKinModal(true);
  };

  const handleCloseNextOfKin = () => setShowNextOfKinModal(false);

  const handleDeleteModal = (employee) => {
    setEmployeeToDelete(employee);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/user/delete/${employeeToDelete.userId}`);

      setData(data.filter((employee) => employee.userId !== employeeToDelete.userId));
      setSearchResults(searchResults.filter((employee) => employee.userId !== employeeToDelete.userId));

      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting employee:", error);
      alert(error.response?.data?.message || "Failed to delete employee.");
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setEmployeeToDelete(null);
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "N/A");

  return (
    <div className="container-fluid">
      <h1 className="emp-info-title">
        <BsFillInfoCircleFill className="settings-icon" /> Employee Information
      </h1>

      <hr />

      <h1 className="emp-info-subtitle">
        <CgProfile className="settings-icon" /> Employee
      </h1>

      <hr />

      <div className="emp-info-content">
        <Link to="/add-emp">
          <Button variant="primary" className="add-emp-button">
            <MdAddchart className="button-icon" /> Add Employee
          </Button>
        </Link>

        <div className="emp-info-actions">
          <Row>
            <Col xs={12} md={6} className="d-flex align-items-center mb-2">
              <Dropdown as={ButtonGroup}>
                <Button variant="primary">Show Entries</Button>

                <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleRowsPerPageChange(10)}>10</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRowsPerPageChange(25)}>25</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRowsPerPageChange(50)}>50</Dropdown.Item>
                  <Dropdown.Item onClick={() => handleRowsPerPageChange(100)}>100</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
              <div className="search-container">
                <Form className="d-flex" onSubmit={handleSubmit}>
                  <Form.Control
                    id="searchQuery"
                    type="text"
                    className="form-control"
                    placeholder="Search Employee"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Form>
              </div>
            </Col>
          </Row>
        </div>

        <div className="table-responsive">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <table className="table table-striped table-style">
              <thead className="tablehead">
                <tr className="table-header-footer">
                  <th className="text-center">SR</th>
                  {/* REMOVED: User ID Header */}
                  <th className="text-center">Employee Name</th>
                  <th className="text-center">Department</th>
                  <th className="text-center">Job Title</th>
                  <th className="text-center">Email</th>
                  <th className="text-center">Gender</th>
                  <th className="text-center">Status</th>
                  {showSalary && <th className="text-center">Salary</th>}
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {searchResults.length > 0 ? (
                  searchResults
                    .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                    .map((employee, index) => (
                      <tr key={employee.userId} style={{ textAlign: "center" }}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        {/* REMOVED: User ID Data Cell */}
                        <td>{DOMPurify.sanitize(employee.name)}</td>
                        <td>{DOMPurify.sanitize(employee.department?.name || "N/A")}</td>
                        <td>{DOMPurify.sanitize(employee.jobTitle || "N/A")}</td>
                        <td>{DOMPurify.sanitize(employee.email || "N/A")}</td>
                        <td>{DOMPurify.sanitize(employee.gender || "N/A")}</td>
                        <td>
                          {employee.status?.toLowerCase() === "active" ? <FcOk /> : DOMPurify.sanitize(employee.status)}
                        </td>
                        {showSalary && <td>{`£${employee.salary || 0}`}</td>}
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="primary"
                              onClick={() => handleShow(employee)}
                              size="sm"
                            >
                              <IoIosEye />
                            </Button>

                            {canEditEmployee(employee) && (
                              <Link to={`/edit-emp/${employee.userId}`}>
                                <Button variant="warning" size="sm">
                                  <FaEdit />
                                </Button>
                              </Link>
                            )}

                            {showDelete && (
                              <Button
                                variant="danger"
                                onClick={() => handleDeleteModal(employee)}
                                size="sm"
                              >
                                <RiDeleteBin5Line />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan={totalColumns}>{loading ? "Loading..." : "No data available"}</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="table-footer">
                <tr className="emp-info-table-footer">
                  <td>Total</td>
                  <td>-</td>
                  <td>{searchResults.length}</td>
                  <td colSpan={totalColumns - 3}></td>
                </tr>
              </tfoot>
            </table>
          )}
          <Row className="mt-2">
            <Col xs={12} md={6} className="d-flex align-items-center">
              <p className="showing-entries">
                Showing {searchResults.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, searchResults.length)} of{" "}
                {searchResults.length} entries
              </p>
            </Col>
            <Col xs={12} md={6} className="d-flex justify-content-end">
              <nav>
                <ul className="pagination">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageRange().map((page) => (
                    <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                      >
                        {page}
                      </a>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      currentPage === Math.ceil(searchResults.length / rowsPerPage) ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                      aria-label="Next"
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </Col>
          </Row>
        </div>
      </div>

      {/* Modal for displaying employee details */}
      {selectedEmployee && (
        <Modal show={showDialog} onHide={handleClose} className="custom-modal" size="lg">
          <Modal.Header style={{ backgroundColor: "#004aad", color: "#ffffff" }}>
            <Modal.Title style={{ color: "#ffffff" }}>Employee Details</Modal.Title>
            <Button variant="close" onClick={handleClose} aria-label="Close" />
          </Modal.Header>

          <Modal.Body>
            <div className="profile-photo-container">
              <h5>Profile Photo</h5>
              {selectedEmployee.profilePhoto ? (
                <img
                  src={`${API_URL}/${selectedEmployee.profilePhoto}`}
                  alt="Employee"
                  className="employee-photo rounded-photo"
                />
              ) : (
                <p>No profile photo available.</p>
              )}
            </div>

            <div className="employee-info">
              <Row>
                <Col md={6}>
                  <p><strong>Name:</strong> {DOMPurify.sanitize(selectedEmployee.name)}</p>
                  <p><strong>Role:</strong> {DOMPurify.sanitize(selectedEmployee.role)}</p>
                  <p><strong>User ID:</strong> {DOMPurify.sanitize(selectedEmployee.userId)}</p>
                  <p><strong>User Name:</strong> {DOMPurify.sanitize(selectedEmployee.username)}</p>
                  <p><strong>Email:</strong> {DOMPurify.sanitize(selectedEmployee.email || "N/A")}</p>
                  <p><strong>Department:</strong> {DOMPurify.sanitize(selectedEmployee.department?.name || "N/A")}</p>
                  <p><strong>Job Title:</strong> {DOMPurify.sanitize(selectedEmployee.jobTitle || "N/A")}</p>
                  {showSalary && (
                    <>
                      <p><strong>Salary:</strong> {`£${selectedEmployee.salary || 0}`}</p>
                      <p><strong>Hourly Rate:</strong> {`£${selectedEmployee.hourlyRate || 0}`}</p>
                    </>
                  )}
                  <p><strong>Gender:</strong> {DOMPurify.sanitize(selectedEmployee.gender || "N/A")}</p>
                  <p><strong>Address:</strong> {DOMPurify.sanitize(selectedEmployee.address || "N/A")}</p>
                </Col>

                <Col md={6}>
                  <p><strong>Status:</strong> {DOMPurify.sanitize(selectedEmployee.status)}</p>
                  <p><strong>Marital Status:</strong> {DOMPurify.sanitize(selectedEmployee.maritalstatus || "N/A")}</p>
                  <p><strong>Join Date:</strong> {formatDate(selectedEmployee.joinDate)}</p>
                  <p><strong>Birthday:</strong> {formatDate(selectedEmployee.birthday)}</p>
                  <p><strong>Work Description:</strong> {DOMPurify.sanitize(selectedEmployee.workDescription || "N/A")}</p>
                  <p><strong>Work Experience:</strong> {DOMPurify.sanitize(selectedEmployee.workExperience || "N/A")}</p>
                </Col>
              </Row>
            </div>

            <div className="next-of-kin-section">
              <h5
                style={{ marginTop: "20px", paddingBottom: "10px", cursor: "pointer" }}
                onClick={() => handleShowNextOfKin(selectedEmployee)}
                className="next-of-kin-heading"
              >
                <strong>View Next of Kin Info</strong> <IoIosArrowForward className="ms-2" />
              </h5>
            </div>

            <div className="documents-section">
              <h5 style={{ marginTop: "20px", borderBottom: "1px solid #ddd", paddingBottom: "10px" }}>
                Documents
              </h5>
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Personal Documents</h6>
                      {selectedEmployee.cv ? (
                        <a href={`${API_URL}/${selectedEmployee.cv}`} target="_blank" rel="noopener noreferrer">
                          Download Personal Documents
                        </a>
                      ) : (
                        <p>No PD available.</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Company data protection and other Contracts</h6>
                      {selectedEmployee.pastWorkCertificate ? (
                        <a href={`${API_URL}/${selectedEmployee.pastWorkCertificate}`} target="_blank" rel="noopener noreferrer">
                          View Company Contracts
                        </a>
                      ) : (
                        <p>No Company Contracts available.</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>HandBook and Employment Contracts</h6>
                      {selectedEmployee.idCardBack ? (
                        <a href={`${API_URL}/${selectedEmployee.idCardBack}`} target="_blank" rel="noopener noreferrer">
                          View Contracts
                        </a>
                      ) : (
                        <p>No Contracts available.</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xs={12} md={6} className="mb-3">
                  <Card className="custom-card">
                    <Card.Body>
                      <h6>Health and Safety Certificates</h6>
                      {selectedEmployee.idCardFront ? (
                        <a href={`${API_URL}/${selectedEmployee.idCardFront}`} target="_blank" rel="noopener noreferrer">
                          View Certificate
                        </a>
                      ) : (
                        <p>No Certificate available.</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Next of Kin Modal */}
      {selectedEmployee && (
        <Modal show={showNextOfKinModal} onHide={handleCloseNextOfKin} size="md" centered>
          <Modal.Header closeButton style={{ backgroundColor: "#004aad", color: "#ffffff" }}>
            <Modal.Title style={{ color: "#ffffff" }}>Next of Kin Information</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="next-of-kin-details">
              <h6 className="mb-3 text-center">{DOMPurify.sanitize(selectedEmployee.name)}'s Next of Kin</h6>

              <div className="detail-item mb-2">
                <strong>Next of Kin Name:</strong>
                <p>{DOMPurify.sanitize(selectedEmployee.nextOfKinName || "Not provided")}</p>
              </div>
              <div className="detail-item mb-2">
                <strong>Relationship:</strong>
                <p>{DOMPurify.sanitize(selectedEmployee.nextOfKinRelationship || "Not provided")}</p>
              </div>
              <div className="detail-item mb-2">
                <strong>Next of Kin Phone:</strong>
                <p>{DOMPurify.sanitize(selectedEmployee.nextOfKinPhone || "Not provided")}</p>
              </div>
              <div className="detail-item mb-2">
                <strong>Next of Kin Email:</strong>
                <p>{DOMPurify.sanitize(selectedEmployee.nextOfKinEmail || "Not provided")}</p>
              </div>
              <div className="detail-item mb-2">
                <strong>Next of Kin Address:</strong>
                <p>{DOMPurify.sanitize(selectedEmployee.nextOfKinAddress || "Not provided")}</p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNextOfKin}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Delete confirmation modal */}
      {employeeToDelete && (
        <Modal show={showDeleteModal} onHide={closeDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete {DOMPurify.sanitize(employeeToDelete.name)}?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDeleteModal}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteConfirmed} disabled={isDeleting}>
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}